import { Autocomplete } from "@mui/material";
import { CssTextField } from "../../../../components/form/CssField";
import { v4 as uuidv4 } from "uuid";
import {
  fetchMapBoxSearch,
  fetchMapBoxRetrieve,
} from "../../../../queries/integrations/mapbox";
import { useMemo, useRef, useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { FormValues } from "../../../Form";


interface Suggestion {
  label: string;
  id: string;
}

interface AutocompleteInputProps {
  input_name: "home" | "work";
  proximity?: [longitude: number, latitude: number];
}

// Custom hook to get window width
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array to run effect only once

  return width;
}


const AutocompleteInput = ({
  input_name,
  proximity,
}: AutocompleteInputProps) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const sessionToken = useRef<string>(uuidv4());
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const updateAddresses = ({target: {value}}: React.ChangeEvent<HTMLInputElement>): void => {
    
    fetchMapBoxSearch({
      queryKey: ["MapBoxSearch", sessionToken.current, value, proximity],
    }).then(({ suggestions }) => {
      const mappedSuggestions = suggestions.map((suggestion) => {
        return {
          label: suggestion.feature_type == "street" ? `${suggestion.name},${suggestion.place_formatted}` : suggestion.full_address,
          id: suggestion.mapbox_id,
        };
      });
      setSuggestions(mappedSuggestions);
    });
  };

  const inputValue = useMemo(
    () => ({ label: values.addresses[input_name].label, id: "" }),
    [values],
  );

  const fetchPlaceAndUpdateValues = (
    _: unknown,
    optionData: Suggestion | null,
  ): void => {
    fetchMapBoxRetrieve({
      queryKey: ["MapBoxRetrieve", sessionToken.current, optionData?.id || ""],
    }).then((response) => {
      const retrieved_address = response.features[0];
      setFieldValue("addresses." + input_name, {
        latitude: retrieved_address.properties.coordinates.latitude,
        longitude: retrieved_address.properties.coordinates.longitude,
        city: retrieved_address.properties.context.place.name,
        zipcode: retrieved_address.properties.context.postcode.name,
        label: retrieved_address.properties.full_address,
      });
    });
  };

  const windowWidth = useWindowWidth();
  // Example condition based on window width
  const shouldDisplayComponent = windowWidth > 900; // Example threshold

  return (
    <Autocomplete
      value={inputValue}
      id={"addresses-" + input_name}
      options={suggestions}

      renderInput={(params) => (
        <CssTextField
          {...params}
          sx={{ width: shouldDisplayComponent ? 600 : "95%" }}
        />
      )}

      getOptionLabel={(option) => option.label}
      onInput={updateAddresses}
      onChange={fetchPlaceAndUpdateValues}
      filterOptions={(x) => x}
      
    />
  );
};

export { AutocompleteInput };
